/**
 * Repair Order technician work-timer rules.
 *
 * Server timestamps are authoritative. Live elapsed time is derived from
 * startedAt and the current instant (or stoppedAt). Duration is never
 * accumulated by incrementing a stored counter.
 *
 * Timezone authority is Asia/Singapore.
 *
 * Assumption: a session continued past the 18:15 SGT end-of-day stop is
 * automatically terminated at 23:59 SGT the same Singapore day. No existing
 * company-wide scheduling contract defines a later cutoff, and an unattended
 * timer must not run overnight.
 */

import { hasRoleCapability, type UserRoleFields } from "./userRoles";
import {
  addSingaporeCalendarDays,
  getSingaporeDateKeyFromInstant,
  isSingaporeWeekday,
} from "./timezone";

export const RO_WORK_TIMER_FINAL_CUTOFF_LABEL = "11:59 PM SGT";

export const RO_COMPLETION_REPORT_REQUIRED_MESSAGE =
  "Enter the customer-facing service report before completing the repair.";

/** Statuses on which a technician may run a work timer. Diagnosis times assessment; repair statuses time approved work. */
export const WORK_START_STATUSES = ["diagnosis", "in_progress", "pending_parts"] as const;

export const TERMINAL_TIMER_STATUSES = [
  "customer_declined",
  "repair_complete",
  "awaiting_payment",
  "awaiting_collection",
  "closed",
] as const;

export const MANUAL_STOP_REASONS = [
  { value: "repair_completed", label: "Repair completed", completesRepair: true, notesRequired: false },
  { value: "waiting_for_parts", label: "Waiting for parts", completesRepair: false, notesRequired: false },
  { value: "further_testing_required", label: "Further testing required", completesRepair: false, notesRequired: false },
  { value: "end_of_day", label: "End of day", completesRepair: false, notesRequired: false },
  { value: "other", label: "Other", completesRepair: false, notesRequired: true },
] as const;

export type ManualStopReason = (typeof MANUAL_STOP_REASONS)[number]["value"];

export const STOP_SOURCES = [
  "approval_changed",
  "manual",
  "end_of_day_automatic",
  "final_cutoff_automatic",
  "admin_recovery",
] as const;

export type StopSource = (typeof STOP_SOURCES)[number];

export const AUTOMATIC_STOP_REASON_LABELS: Record<
  "end_of_day_automatic" | "final_cutoff_automatic",
  string
> = {
  end_of_day_automatic: "Automatically stopped at end of day",
  final_cutoff_automatic: "Automatically stopped at 11:59 PM SGT",
};

export const PAUSE_STOP_REASONS = MANUAL_STOP_REASONS.filter((reason) => !reason.completesRepair).map(
  (reason) => reason.value,
);

export type RoWorkSessionSnapshot = {
  id: number;
  sessionUuid: string;
  repairOrderId: number;
  technicianUserId: number;
  technicianName: string;
  startedAt: Date;
  stoppedAt: Date | null;
  durationSeconds: number | null;
  stopReason: string | null;
  stopNotes: string | null;
  stopSource: StopSource | null;
  continuationConfirmedAt: Date | null;
  continuationConfirmedByUserId: number | null;
};

export type CommercialViewer = UserRoleFields & {
  userId?: number | null;
};

const SUGGESTED_INTERNAL_COST_FIELDS = ["repairCostInternal"] as const;
const INTERNAL_COMMERCIAL_FIELDS = ["estimatedCost", "margin", "marginAmount", "marginPercent"] as const;
const CUSTOMER_FACING_AMOUNT_FIELDS = [
  "customerCharge",
  "actualCost",
  "sellingPrice",
  "roInvoiceAmount",
] as const;

export function elapsedSeconds(startedAt: Date, endedAt: Date): number {
  const delta = endedAt.getTime() - startedAt.getTime();
  if (!Number.isFinite(delta) || delta <= 0) return 0;
  return Math.floor(delta / 1000);
}

export function formatElapsedSeconds(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, "0")).join(":");
}

export function singaporeInstant(dateKey: string, hour: number, minute: number, second = 0): Date {
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  const ss = String(second).padStart(2, "0");
  return new Date(`${dateKey}T${hh}:${mm}:${ss}+08:00`);
}

export function canStartWorkOnStatus(status: string): string | null {
  if (TERMINAL_TIMER_STATUSES.includes(status as (typeof TERMINAL_TIMER_STATUSES)[number])) {
    return "This repair order is closed, declined, or otherwise finished and cannot start a new work timer.";
  }
  if (!WORK_START_STATUSES.includes(status as (typeof WORK_START_STATUSES)[number])) {
    return "Work cannot begin until the repair has been approved to proceed.";
  }
  return null;
}

export function isAssignedTechnician(
  viewer: { userId?: number | null },
  job: { assignedTechnicianId?: number | null },
): boolean {
  return viewer.userId != null && job.assignedTechnicianId != null && viewer.userId === job.assignedTechnicianId;
}

export function canControlOwnSession(
  viewer: CommercialViewer,
  job: { assignedTechnicianId?: number | null },
): boolean {
  return hasRoleCapability(viewer, ["admin"]) || isAssignedTechnician(viewer, job);
}

/**
 * Generic RO status change: claim the RO only when nobody is assigned yet.
 * An existing technician (by id or legacy name) is never overwritten, so a status
 * change cannot silently reassign the RO or orphan that technician's running timer.
 */
export function statusChangeAssigneePatch(
  job: { assignedTechnicianId?: number | null; assignedTechnicianName?: string | null },
  user: { id: number; name?: string | null },
): { assignedTechnicianId: number; assignedTechnicianName: string } | Record<string, never> {
  const hasTechnician = job.assignedTechnicianId != null || Boolean(String(job.assignedTechnicianName ?? "").trim());
  const name = String(user.name ?? "").trim();
  if (hasTechnician || !name) return {};
  return { assignedTechnicianId: user.id, assignedTechnicianName: name };
}

export function canAdminRecover(viewer: UserRoleFields | null | undefined): boolean {
  return hasRoleCapability(viewer, ["admin"]);
}

export function canReadWorkTimerPortal(viewer: UserRoleFields | null | undefined): boolean {
  if (!viewer) return false;
  const roles = hasRoleCapability(viewer, [
    "admin",
    "service",
    "finance",
    "sales",
    "programming",
    "installer",
    "inventory",
    "club",
  ]);
  return roles;
}

export function isAdminOrFinance(viewer: UserRoleFields | null | undefined): boolean {
  return hasRoleCapability(viewer, ["admin", "finance"]);
}

export function canSeeCustomerFacingAmounts(viewer: UserRoleFields | null | undefined): boolean {
  return hasRoleCapability(viewer, ["admin", "finance", "sales"]);
}

export function canViewSuggestedInternalRepairCost(
  viewer: CommercialViewer | null | undefined,
  job: { assignedTechnicianId?: number | null },
): boolean {
  if (!viewer) return false;
  if (isAdminOrFinance(viewer)) return true;
  return isAssignedTechnician(viewer, job);
}

export function canEnterSuggestedInternalRepairCost(
  viewer: CommercialViewer | null | undefined,
  job: { assignedTechnicianId?: number | null },
): boolean {
  if (!viewer) return false;
  if (canViewSuggestedInternalRepairCost(viewer, job)) return true;
  return job.assignedTechnicianId == null && hasRoleCapability(viewer, ["service"]);
}

export function projectRepairOrderCommercialFields<T extends Record<string, unknown>>(
  job: T,
  viewer: CommercialViewer | null | undefined,
): T {
  if (isAdminOrFinance(viewer)) return job;
  const copy: Record<string, unknown> = { ...job };
  const assignedJob = {
    assignedTechnicianId:
      typeof job.assignedTechnicianId === "number" ? job.assignedTechnicianId : null,
  };
  if (!canViewSuggestedInternalRepairCost(viewer, assignedJob)) {
    for (const field of SUGGESTED_INTERNAL_COST_FIELDS) delete copy[field];
  }
  for (const field of INTERNAL_COMMERCIAL_FIELDS) delete copy[field];
  if (!canSeeCustomerFacingAmounts(viewer)) {
    for (const field of CUSTOMER_FACING_AMOUNT_FIELDS) delete copy[field];
  }
  return copy as T;
}

export function assertManualStop(input: {
  reason: string;
  notes?: string | null;
  customerReport?: string | null;
}): { reason: ManualStopReason; notes: string | null; completesRepair: boolean } {
  const match = MANUAL_STOP_REASONS.find((reason) => reason.value === input.reason);
  if (!match) throw new Error("Choose a stop reason.");
  const notes = String(input.notes ?? "").trim();
  if (match.notesRequired && !notes) throw new Error("Other requires a note.");
  if (match.completesRepair && !String(input.customerReport ?? "").trim()) {
    throw new Error("Repair completed requires the customer-facing service report.");
  }
  return { reason: match.value, notes: notes || null, completesRepair: match.completesRepair };
}

export function isCompleteCustomerServiceReport(customerReport: string | null | undefined): boolean {
  return String(customerReport ?? "").trim().length > 0;
}

/** Expected client validation: keep a required report correction inline, not as a system error toast. */
export function roCompletionReportValidationError(value: unknown): string | null {
  return isCompleteCustomerServiceReport(String(value ?? ""))
    ? null
    : RO_COMPLETION_REPORT_REQUIRED_MESSAGE;
}

export function continuationAppliesToSession(
  session: Pick<RoWorkSessionSnapshot, "startedAt" | "continuationConfirmedAt">,
): boolean {
  if (!session.continuationConfirmedAt) return false;
  const startDay = getSingaporeDateKeyFromInstant(session.startedAt);
  const confirmedDay = getSingaporeDateKeyFromInstant(session.continuationConfirmedAt);
  return Boolean(startDay && confirmedDay && startDay === confirmedDay);
}

export type AutomaticStopDecision = {
  source: "end_of_day_automatic" | "final_cutoff_automatic";
  reason: string;
  singaporeDay: string;
};

/**
 * Sessions already running before 18:15 SGT stop then unless a YES was stored.
 * Continued sessions stop at 23:59 SGT. Late scheduler passes use the same rule
 * so a closed browser cannot leave a timer running.
 */
export function automaticStopDecision(
  session: Pick<RoWorkSessionSnapshot, "startedAt" | "stoppedAt" | "continuationConfirmedAt">,
  now: Date,
): AutomaticStopDecision | null {
  if (session.stoppedAt) return null;
  const startDay = getSingaporeDateKeyFromInstant(session.startedAt);
  if (!startDay) return null;
  const eod = singaporeInstant(startDay, 18, 15);
  const finalCutoff = singaporeInstant(startDay, 23, 59);
  const confirmed = continuationAppliesToSession(session);
  if (now.getTime() >= finalCutoff.getTime()) {
    const startedAfterEodPrompt = session.startedAt.getTime() >= eod.getTime();
    const finalCutoffStop = confirmed || startedAfterEodPrompt;
    return {
      source: finalCutoffStop ? "final_cutoff_automatic" : "end_of_day_automatic",
      reason: finalCutoffStop
        ? AUTOMATIC_STOP_REASON_LABELS.final_cutoff_automatic
        : AUTOMATIC_STOP_REASON_LABELS.end_of_day_automatic,
      singaporeDay: startDay,
    };
  }
  if (now.getTime() >= eod.getTime() && session.startedAt.getTime() < eod.getTime() && !confirmed) {
    return {
      source: "end_of_day_automatic",
      reason: AUTOMATIC_STOP_REASON_LABELS.end_of_day_automatic,
      singaporeDay: startDay,
    };
  }
  return null;
}

export function continuationPromptDue(
  session: Pick<RoWorkSessionSnapshot, "startedAt" | "stoppedAt" | "continuationConfirmedAt">,
  now: Date,
): boolean {
  if (session.stoppedAt || continuationAppliesToSession(session)) return false;
  const startDay = getSingaporeDateKeyFromInstant(session.startedAt);
  const nowDay = getSingaporeDateKeyFromInstant(now);
  if (!startDay || startDay !== nowDay) return false;
  const promptAt = singaporeInstant(startDay, 18, 0);
  const eod = singaporeInstant(startDay, 18, 15);
  return (
    session.startedAt.getTime() < eod.getTime() &&
    now.getTime() >= promptAt.getTime() &&
    now.getTime() < eod.getTime()
  );
}

export function continuationConfirmationAllowed(
  session: Pick<RoWorkSessionSnapshot, "startedAt" | "stoppedAt" | "continuationConfirmedAt">,
  now: Date,
): string | null {
  if (session.stoppedAt) return "This work session has already stopped.";
  if (continuationAppliesToSession(session)) return null;
  const startDay = getSingaporeDateKeyFromInstant(session.startedAt);
  if (!startDay) return "This work session has no start time.";
  const promptAt = singaporeInstant(startDay, 18, 0);
  const finalCutoff = singaporeInstant(startDay, 23, 59);
  const eod = singaporeInstant(startDay, 18, 15);
  if (session.startedAt.getTime() >= eod.getTime()) {
    return "This session started after the end-of-day prompt.";
  }
  if (now.getTime() < promptAt.getTime()) return "The end-of-day prompt is not open yet.";
  if (now.getTime() >= finalCutoff.getTime()) return "The final cutoff has passed.";
  return null;
}

export function previousWeekdayDateKey(dateKey: string): string {
  let cursor = addSingaporeCalendarDays(dateKey, -1);
  for (let i = 0; i < 7; i += 1) {
    const probe = singaporeInstant(cursor, 12, 0);
    if (isSingaporeWeekday(probe)) return cursor;
    cursor = addSingaporeCalendarDays(cursor, -1);
  }
  return cursor;
}

export function historyOverlapSeconds(
  session: Pick<RoWorkSessionSnapshot, "startedAt" | "stoppedAt">,
  dateKey: string,
  now: Date,
): number {
  const dayStart = singaporeInstant(dateKey, 0, 0).getTime();
  const dayEnd = singaporeInstant(addSingaporeCalendarDays(dateKey, 1), 0, 0).getTime();
  const start = session.startedAt.getTime();
  const end = (session.stoppedAt ?? now).getTime();
  const overlapStart = Math.max(start, dayStart);
  const overlapEnd = Math.min(end, dayEnd);
  if (overlapEnd <= overlapStart) return 0;
  return Math.floor((overlapEnd - overlapStart) / 1000);
}

export function stopReasonLabel(reason: string | null | undefined, source: string | null | undefined): string {
  if (source === "end_of_day_automatic") return AUTOMATIC_STOP_REASON_LABELS.end_of_day_automatic;
  if (source === "final_cutoff_automatic") return AUTOMATIC_STOP_REASON_LABELS.final_cutoff_automatic;
  const manual = MANUAL_STOP_REASONS.find((item) => item.value === reason);
  if (manual) return manual.label;
  return reason?.trim() || "—";
}

export type DigestSessionRow = {
  jobNumber: string;
  repairOrderId: number;
  technicianName: string;
  technicianUserId: number;
  startedAt: Date;
  stoppedAt: Date | null;
  stopReason: string | null;
  stopNotes: string | null;
  stopSource: StopSource | null;
  equipment: string;
  customerName: string;
};

export function digestSessionsForDay(rows: DigestSessionRow[], dateKey: string, now: Date): DigestSessionRow[] {
  return rows.filter((row) => historyOverlapSeconds(row, dateKey, now) > 0);
}

export function sumSeconds(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

const COMMERCIAL_KEY_RE =
  /repairCostInternal|customerCharge|actualCost|sellingPrice|estimatedCost|margin|roInvoiceAmount/i;

export function assertNoCommercialFields(value: unknown): void {
  const json = JSON.stringify(value);
  if (COMMERCIAL_KEY_RE.test(json)) {
    throw new Error("Work timer payload included commercial fields.");
  }
}
